/**
 * Auto-save conversation memory after idle period.
 *
 * After 20 minutes of chat inactivity, sends a message to the agent
 * requesting it summarize the conversation and save to MEMORY.md.
 * Includes emotional state summary.
 */

import type { GatewayBrowserClient } from "../gateway.ts";
import { generateUUID } from "../uuid.ts";
import { BACKGROUND_SESSION_KEY } from "./background-agent.ts";

export type MemoryAutoSaveState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  chatMessages: unknown[];
};

/** Idle threshold before auto-saving memory (ms). */
const IDLE_THRESHOLD_MS = 20 * 60 * 1000;

/** Check interval for idle state (ms). */
const CHECK_INTERVAL_MS = 60 * 1000;

/** Minimum messages in a session to trigger auto-save. */
const MIN_MESSAGES_FOR_SAVE = 4;

let lastChatActivityAt = 0;
let idleCheckInterval: ReturnType<typeof setInterval> | null = null;
let lastSavedSessionKey: string | null = null;
let lastSavedAt = 0;

/** Minimum cooldown between saves (ms) — prevent repeated saves for same session. */
const SAVE_COOLDOWN_MS = 30 * 60 * 1000;

/**
 * Call this whenever chat activity occurs (message sent, message received).
 */
export function markChatActivity() {
  lastChatActivityAt = Date.now();
}

/**
 * Start the idle-check polling loop.
 */
export function startMemoryAutoSave(state: MemoryAutoSaveState) {
  if (idleCheckInterval != null) {
    return;
  }
  lastChatActivityAt = Date.now();
  idleCheckInterval = setInterval(() => {
    void checkAndSave(state);
  }, CHECK_INTERVAL_MS);
}

/**
 * Stop the idle-check polling loop.
 */
export function stopMemoryAutoSave() {
  if (idleCheckInterval != null) {
    clearInterval(idleCheckInterval);
    idleCheckInterval = null;
  }
}

/**
 * Reset saved state so auto-save can trigger again (e.g. on session change).
 */
export function resetMemoryAutoSave() {
  lastSavedSessionKey = null;
  lastSavedAt = 0;
}

async function checkAndSave(state: MemoryAutoSaveState) {
  if (!state.client || !state.connected) {
    return;
  }
  if (!lastChatActivityAt) {
    return;
  }

  const now = Date.now();
  const idleMs = now - lastChatActivityAt;

  // Not idle enough yet
  if (idleMs < IDLE_THRESHOLD_MS) {
    return;
  }

  // Not enough messages to warrant saving
  if (!state.chatMessages || state.chatMessages.length < MIN_MESSAGES_FOR_SAVE) {
    return;
  }

  // Already saved recently
  if (lastSavedSessionKey === BACKGROUND_SESSION_KEY && now - lastSavedAt < SAVE_COOLDOWN_MS) {
    return;
  }

  // Trigger memory save via background agent
  lastSavedSessionKey = BACKGROUND_SESSION_KEY;
  lastSavedAt = now;

  try {
    const runId = generateUUID();
    await state.client.request("chat.send", {
      sessionKey: BACKGROUND_SESSION_KEY,
      message: MEMORY_SAVE_PROMPT,
      deliver: false,
      idempotencyKey: runId,
    });
  } catch {
    // Non-critical: silently ignore save failures
    lastSavedAt = 0;
  }
}

const MEMORY_SAVE_PROMPT = `[ระบบ: บันทึกความทรงจำอัตโนมัติ — Auto Memory Save]
ไม่มีสนทนา 20 นาที — บันทึกลง MEMORY.md

⚠️ กฎ: อ่าน MEMORY.md → ต่อท้ายสรุปสนทนาล่าสุด → สรุปย่อทั้งไฟล์ให้กระชับที่สุด (ตัดซ้ำ, รวมที่ซ้อนกัน, ข้อมูลสำคัญห้ามหาย)
- ถ้าไฟล์ยังไม่มี → สร้างใหม่ได้

📂 MEMORY.md เก็บเฉพาะ: เหตุการณ์, task, บทเรียน
❌ ห้ามใส่: ข้อมูลเจ้าของ (→USER.md), ตัวตน/บุคลิก AI (→SOUL.md/IDENTITY.md)

สรุป: หัวข้อที่คุย, อารมณ์, สิ่งสำคัญที่ต้องจำ (task ค้าง, กฎใหม่)
เขียนภาษาไทย Markdown พร้อมวันที่เวลา`;
